import type { EscalationEmailSettings, WriteRejection } from "@estelle/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useId, useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RejectionNotice } from "@/modules/orders/screens/RejectionNotice";

const KEY = ["escalations", "email-settings"] as const;

/**
 * Where the delivery escalation emails go and when the status email is sent
 * (#702): one address, set once, and two times a day in India time that can each
 * be switched off. Every subject starts with "Escalations", so a Gmail rule can
 * forward them.
 */
function Form({ saved }: { saved: EscalationEmailSettings }) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const addressId = useId();
  const [draft, setDraft] = useState(saved);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);
  const [done, setDone] = useState(false);

  const save = useMutation({
    mutationFn: () => data.escalations.saveEmailSettings(draft),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        setDone(true);
        queryClient.setQueryData(KEY, result.data);
      } else {
        setDone(false);
        setRejection(result.rejection);
      }
    },
  });

  const changed = (
    Object.keys(saved) as (keyof EscalationEmailSettings)[]
  ).some((key) => saved[key] !== draft[key]);
  const set = (patch: Partial<EscalationEmailSettings>) => {
    setDone(false);
    setDraft((current) => ({ ...current, ...patch }));
  };
  const fieldError = (name: string): string | undefined =>
    rejection?.kind === "invalid" ? rejection.fields?.[name] : undefined;

  const slot = (
    label: string,
    enabledKey: "morningEnabled" | "eveningEnabled",
    timeKey: "morningTime" | "eveningTime",
  ) => (
    <div className="flex flex-wrap items-center gap-3">
      <Checkbox
        aria-label={`Send the ${label} status email`}
        checked={draft[enabledKey]}
        disabled={save.isPending}
        onCheckedChange={(value) => {
          set({ [enabledKey]: value === true });
        }}
      />
      <span className="w-20 text-sm">{label}</span>
      <Input
        type="time"
        aria-label={`${label} status email time`}
        className="w-32"
        value={draft[timeKey]}
        disabled={save.isPending || !draft[enabledKey]}
        aria-invalid={fieldError(timeKey) ? true : undefined}
        onChange={(event) => {
          set({ [timeKey]: event.target.value });
        }}
      />
      {fieldError(timeKey) ? (
        <p className="text-destructive text-sm">{fieldError(timeKey)}</p>
      ) : null}
    </div>
  );

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Delivery escalation emails</CardTitle>
        <CardDescription>
          The courier email and the status email both go to one address. The
          status email reports what is new since the last one, and is sent even
          when nothing changed, so silence never means it broke. Scheduled
          emails wait out quiet hours; the courier email and &quot;Email me
          status now&quot; send at once.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor={addressId} className="text-sm font-medium">
            Send escalation emails to
          </Label>
          <Input
            id={addressId}
            type="email"
            value={draft.address}
            disabled={save.isPending}
            aria-invalid={fieldError("address") ? true : undefined}
            onChange={(event) => {
              set({ address: event.target.value });
            }}
          />
          {fieldError("address") ? (
            <p className="text-destructive text-sm">{fieldError("address")}</p>
          ) : null}
        </div>

        <fieldset className="space-y-3 rounded-md border p-4">
          <legend className="px-1 text-sm font-medium">
            Status email (India time)
          </legend>
          {slot("Morning", "morningEnabled", "morningTime")}
          {slot("Evening", "eveningEnabled", "eveningTime")}
        </fieldset>

        {rejection ? (
          <RejectionNotice
            rejection={rejection}
            onReload={() => {
              setRejection(null);
            }}
          />
        ) : null}
        {done ? (
          <p
            role="status"
            className="text-sm text-emerald-700 dark:text-emerald-300"
          >
            Saved.
          </p>
        ) : null}

        <Button
          type="button"
          disabled={!changed || save.isPending}
          onClick={() => {
            save.mutate();
          }}
        >
          {save.isPending ? <Loader2 className="animate-spin" /> : null}
          Save
        </Button>
      </CardContent>
    </Card>
  );
}

export function EscalationEmails() {
  const data = useDataSource();
  const query = useQuery({
    queryKey: KEY,
    queryFn: () => data.escalations.emailSettings(),
  });

  if (query.isPending) return <TableSkeleton rows={3} />;
  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  return <Form saved={query.data} />;
}
